import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  Share2,
  Flag,
  Trash2,
  Heart,
  MessageCircle,
  MapPin,
  Send,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '@utils/api';
import { formatActivityDate } from '@utils/helpers';
import { useAuth } from '@context/AuthContext';

const PhotoViewer = ({ photos, index, onClose, onIndexChange, onDeleted }) => {
  const { user } = useAuth();
  const [scale, setScale] = useState(1);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [busy, setBusy] = useState(false);
  const touchRef = useRef({ startX: 0, startDist: 0, pinching: false });

  const photo = photos[index];
  const isUploader = photo && user && String(photo.uploader?._id) === String(user._id);
  const isLegacy = photo?.isLegacy || String(photo?._id || '').startsWith('legacy-');

  useEffect(() => {
    if (!photo) return;
    setLiked(Boolean(photo.likedByMe));
    setLikeCount(photo.likeCount || 0);
    setScale(1);
    setShowComments(false);
    setComments([]);
    setCommentText('');
  }, [photo?._id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowLeft' && index > 0) onIndexChange?.(index - 1);
      if (e.key === 'ArrowRight' && index < photos.length - 1) onIndexChange?.(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, photos.length, onClose, onIndexChange]);

  const loadComments = useCallback(async () => {
    if (!photo || isLegacy) return;
    try {
      const { data } = await api.get(`/memories/photos/${photo._id}/comments`);
      setComments(data.comments || []);
    } catch {
      /* ignore */
    }
  }, [photo, isLegacy]);

  useEffect(() => {
    if (showComments) loadComments();
  }, [showComments, loadComments]);

  if (!photo) return null;

  const handleLike = async () => {
    if (isLegacy) {
      toast.error('Likes are not available for older photos');
      return;
    }
    try {
      const { data } = await api.post(`/memories/photos/${photo._id}/like`);
      setLiked(data.liked);
      setLikeCount(data.likeCount);
    } catch {
      toast.error('Could not update like');
    }
  };

  const handleDownload = async () => {
    try {
      const res = await fetch(photo.url);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `yaarvi-memory-${photo._id}.jpg`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      window.open(photo.url, '_blank');
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: photo.activity?.title || 'Yaarvi memory',
          url: photo.url,
        });
      } else {
        await navigator.clipboard.writeText(photo.url);
        toast.success('Link copied');
      }
    } catch {
      /* cancelled */
    }
  };

  const handleReport = async () => {
    if (isLegacy) return;
    try {
      setBusy(true);
      await api.post(`/memories/photos/${photo._id}/report`, {
        reason: 'inappropriate',
        description: 'Reported from photo viewer',
      });
      toast.success('Report submitted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not report');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!isUploader || isLegacy) return;
    if (!window.confirm('Delete this photo?')) return;
    try {
      setBusy(true);
      await api.delete(`/memories/photos/${photo._id}`);
      toast.success('Photo deleted');
      onDeleted?.(photo._id);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete');
    } finally {
      setBusy(false);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || isLegacy) return;
    try {
      const { data } = await api.post(`/memories/photos/${photo._id}/comments`, {
        text: commentText.trim(),
      });
      setComments((prev) => [data.comment, ...prev]);
      setCommentText('');
    } catch {
      toast.error('Could not post comment');
    }
  };

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      touchRef.current = {
        startX: 0,
        startDist: Math.hypot(dx, dy),
        pinching: true,
        startScale: scale,
      };
      return;
    }
    touchRef.current = {
      startX: e.touches[0].clientX,
      startDist: 0,
      pinching: false,
    };
  };

  const onTouchMove = (e) => {
    if (touchRef.current.pinching && e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const next = Math.min(4, Math.max(1, (touchRef.current.startScale || 1) * (dist / touchRef.current.startDist)));
      setScale(next);
    }
  };

  const onTouchEnd = (e) => {
    if (touchRef.current.pinching) {
      touchRef.current.pinching = false;
      return;
    }
    if (scale > 1.05) return;
    const dx = (e.changedTouches[0]?.clientX || 0) - touchRef.current.startX;
    if (Math.abs(dx) > 60) {
      if (dx < 0 && index < photos.length - 1) onIndexChange?.(index + 1);
      if (dx > 0 && index > 0) onIndexChange?.(index - 1);
    }
  };

  const activity = photo.activity || {};
  const location = activity.location?.address || '';

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[300] flex flex-col bg-black/95"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="flex items-center justify-between px-3 py-3 safe-bottom">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-white/10 p-2 text-white backdrop-blur-md"
          >
            <X size={22} />
          </button>
          <div className="flex items-center gap-1">
            <button type="button" onClick={handleDownload} className="rounded-full bg-white/10 p-2 text-white">
              <Download size={18} />
            </button>
            <button type="button" onClick={handleShare} className="rounded-full bg-white/10 p-2 text-white">
              <Share2 size={18} />
            </button>
            {!isLegacy && (
              <button
                type="button"
                disabled={busy}
                onClick={handleReport}
                className="rounded-full bg-white/10 p-2 text-white"
              >
                <Flag size={18} />
              </button>
            )}
            {isUploader && !isLegacy && (
              <button
                type="button"
                disabled={busy}
                onClick={handleDelete}
                className="rounded-full bg-white/10 p-2 text-red-400"
              >
                <Trash2 size={18} />
              </button>
            )}
          </div>
        </div>

        <div
          className="relative flex flex-1 items-center justify-center overflow-hidden"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {index > 0 && (
            <button
              type="button"
              className="absolute left-2 z-10 hidden rounded-full bg-white/10 p-2 text-white sm:flex"
              onClick={() => onIndexChange?.(index - 1)}
            >
              <ChevronLeft size={24} />
            </button>
          )}
          <motion.img
            key={photo._id}
            src={photo.url}
            alt=""
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale }}
            transition={{ duration: 0.25 }}
            className="max-h-full max-w-full object-contain select-none"
            draggable={false}
            style={{ transformOrigin: 'center center' }}
          />
          {index < photos.length - 1 && (
            <button
              type="button"
              className="absolute right-2 z-10 hidden rounded-full bg-white/10 p-2 text-white sm:flex"
              onClick={() => onIndexChange?.(index + 1)}
            >
              <ChevronRight size={24} />
            </button>
          )}
        </div>

        <div className="bg-gradient-to-t from-black via-black/80 to-transparent px-4 pb-6 pt-8 text-white">
          <p className="text-base font-bold">
            {activity.emoji ? `${activity.emoji} ` : ''}
            {activity.title || 'Memory'}
          </p>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-white/70">
            {activity.dateTime && <span>{formatActivityDate(activity.dateTime)}</span>}
            {location && (
              <span className="inline-flex items-center gap-1">
                <MapPin size={11} />
                {location}
              </span>
            )}
            {photo.uploader?.name && <span>Uploaded by {photo.uploader.name}</span>}
            {activity.participantCount != null && (
              <span>{activity.participantCount} participants</span>
            )}
          </div>
          <div className="mt-3 flex items-center gap-4">
            <button
              type="button"
              onClick={handleLike}
              className="inline-flex items-center gap-1.5 text-sm font-semibold"
            >
              <Heart
                size={20}
                className={liked ? 'fill-rose-500 text-rose-500' : 'text-white'}
              />
              {likeCount}
            </button>
            <button
              type="button"
              onClick={() => setShowComments((v) => !v)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold"
            >
              <MessageCircle size={20} />
              Comment
            </button>
          </div>

          {showComments && (
            <div className="mt-3 max-h-40 overflow-y-auto rounded-2xl bg-white/10 p-3 backdrop-blur-md">
              {comments.length === 0 ? (
                <p className="text-xs text-white/50">No comments yet</p>
              ) : (
                <ul className="space-y-2">
                  {comments.map((c) => (
                    <li key={c._id} className="text-xs">
                      <span className="font-semibold">{c.user?.name || 'User'}: </span>
                      <span className="text-white/80">{c.text}</span>
                    </li>
                  ))}
                </ul>
              )}
              {!isLegacy && (
                <form onSubmit={handleComment} className="mt-2 flex gap-2">
                  <input
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Add a comment…"
                    maxLength={300}
                    className="flex-1 rounded-xl border-0 bg-white/10 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-white/30"
                  />
                  <button type="submit" className="rounded-xl bg-white/20 p-2">
                    <Send size={16} />
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PhotoViewer;
